import { ChevronLeft, Search } from 'lucide-react'
import { useLocale } from '../i18n/context'

// The one permanently pinned element on the mobile deck screen (ADR-067
// Decision 1 / brief §4). Everything else above the feed — progress, study
// launchers, the daily-learning toggle — scrolls away with the cards; this
// strip does not, because with the global bottom navigation gone it is the
// only exit from the screen apart from the device's back gesture.
export const DECK_TOP_STRIP_HEIGHT = 48

// Height including the top safe-area inset, as a CSS length. Consumers pad
// their content by exactly this so nothing starts underneath the strip.
export const DECK_TOP_STRIP_CLEARANCE =
  `calc(${DECK_TOP_STRIP_HEIGHT}px + env(safe-area-inset-top, 0px))`

// Marks the strip in the DOM so the field-focus rule (brief §7: the focused
// field's top edge is brought to the top of the free space) can measure where
// the free space actually begins. Measured rather than recomputed from
// DECK_TOP_STRIP_HEIGHT because the strip's real bottom includes the top
// safe-area inset, which is only knowable at runtime.
export const DECK_TOP_STRIP_PROBE_ATTR = 'data-deck-top-strip'

interface Props {
  title: string
  /** Right-hand mode word — `SYSTEM`, `EDITING`, `FILTERED`, … Omit for none. */
  modeWord?: string
  backLabel: string
  onBack: () => void
  /**
   * Search's one entry point (brief §15, 2026-08-04 rework). Lives here rather
   * than in the view/edit bottom bars or the ⋯ menu specifically because this
   * strip is the one piece of chrome present, unchanged, in every state search
   * needs to work from — view mode, editing mode, and a read-only system deck
   * — so a single control here is a single control everywhere at once, with no
   * per-mode duplication. Omit to render no button at all (desktop, where this
   * whole strip is hidden by `md:hidden`, never needs one).
   */
  onToggleSearch?: () => void
  /** Whether the search panel is currently open — drives the icon's pressed
   * styling so the one control also shows its own state. */
  searchOpen?: boolean
  /** Nothing to search in an empty deck. Dimmed, not hidden — a control that
   * disappears when a deck empties out moves everything beside it. */
  searchDisabled?: boolean
}

export default function DeckTopStrip({
  title, modeWord, backLabel, onBack, onToggleSearch, searchOpen, searchDisabled,
}: Props) {
  const { tr } = useLocale()
  return (
    <div
      className="md:hidden"
      {...{ [DECK_TOP_STRIP_PROBE_ATTR]: '' }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 25,
        height: DECK_TOP_STRIP_CLEARANCE,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingRight: 4,
        display: 'flex', alignItems: 'center', gap: 4,
        // Chrome plane, not a surface (DESIGN_SYSTEM.md Wave 4 §7). --bg-surface
        // was wrong for the same reason --bg-header is forbidden here: in dark
        // both resolve to #161B22, identical to the cards scrolling underneath,
        // so the strip had no plane of its own. The hairline is load-bearing —
        // in light --bg-chrome and --bg-surface are the same white, and the
        // border is the only thing separating the strip from the feed.
        background: 'var(--bg-chrome)',
        borderBottom: '1px solid var(--border-chrome)',
      }}
    >
      <button
        onClick={onBack}
        aria-label={backLabel}
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-primary)', padding: 0,
        }}
      >
        <ChevronLeft size={22} />
      </button>

      {/* Truncates rather than wrapping: the strip's height is fixed and a
          two-line title would either clip or push the feed down. */}
      <div
        style={{
          flex: 1, minWidth: 0,
          fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 600,
          color: 'var(--text-primary)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {title}
      </div>

      {modeWord && (
        <span
          style={{
            flexShrink: 0, marginRight: 8,
            fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--text-secondary)',
          }}
        >
          {modeWord}
        </span>
      )}

      {onToggleSearch && (
        <button
          onClick={onToggleSearch}
          disabled={searchDisabled}
          aria-label={tr.deckDetail.deckSearchToggleAria}
          aria-pressed={!!searchOpen}
          style={{
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40,
            background: searchOpen ? 'var(--accent-soft)' : 'none',
            borderRadius: 'var(--radius-pill)',
            border: 'none', cursor: searchDisabled ? 'default' : 'pointer',
            color: searchDisabled ? 'var(--text-secondary)' : (searchOpen ? 'var(--accent)' : 'var(--text-primary)'),
            opacity: searchDisabled ? 0.4 : 1,
            padding: 0,
          }}
        >
          <Search size={19} strokeWidth={2.1} />
        </button>
      )}
    </div>
  )
}
